
	import {Component, Prop, Vue, Watch} from 'vue-property-decorator';
	import IResource from "@/IResource";
	import IAvailability, { IAvailabilityValue } from "@/IAvailability";

	interface CalendarDay {
		date: Date;
		dateKey: string;
		isToday: boolean;
		isCurrentMonth: boolean;
		isPast: boolean;
		isSelected: boolean;
	}

	@Component({
		components: {},
	})
	export default class MultidayCalendarDay extends Vue {

		@Prop({
			required: true,
			type: Object
		})
		public day!: CalendarDay | null;

		get availability(): IAvailability {
			return this.$store.state.availability;
		}

		get resource(): IResource {
			return this.$store.state.resource;
		}

		get classes() {
			if (this.day) {
				let classes = {
					'day--past': this.day.isPast,
					'day--current-month': this.day.isCurrentMonth,
					'day--today': this.day.isToday,
					'day--selected': this.day.isSelected && this.day.isCurrentMonth
				};
				if (this.day.isCurrentMonth && !this.day.isPast) {
					Vue.set(classes, 'day--state-morning-' + this.reservationState[0], true)
					Vue.set(classes, 'day--state-evening-' + this.reservationState[1], true)
				}
				if (this.day.isCurrentMonth) {
					Vue.set(classes, 'day--' + this.day.dateKey, true)
				}
				return classes;
			}
			return {};
		}

		get reservationState(): [string, string] {
			// S = Selected, B = Booked, F = Free, N = New, L = Locked
			let morning = 'F';
			let evening = 'F';
			if (this.day && this.availability) {
				if (this.day.isSelected) {
					morning = 'S';
					evening = 'S';
				}
				let checkoutTime: number = this.resource.checkin_time_enabled ? this.resource.checkout_time : 720;
				let checkinTime: number = this.resource.checkin_time_enabled ? this.resource.checkin_time : 720;
				if (this.day.dateKey in this.availability) {
					let slots = this.availability[this.day.dateKey];
					slots = slots.sort((a, b) => {
						// paint locktimes in the background
						if (!a[2] || !b[2]) return 0;
						if (a[2].status === 'locked') return -1;
						if (b[2].status === 'locked') return 1;
						return 0;
					})
					slots.forEach((slot: IAvailabilityValue) => {
						if (slot[0] < checkoutTime || slot[1] <= checkoutTime) {
							if (slot[2] && slot[2]?.status === 'new') {
								morning = 'N';
							} else if (slot[2] && slot[2]?.status === 'locked') {
								morning = 'L';
							} else {
								morning = 'B';
							}
						}
						if (slot[0] >= checkinTime || slot[1] > checkinTime) {
							if (slot[2] && slot[2]?.status === 'new') {
								evening = 'N';
							} else if (slot[2] && slot[2]?.status === 'locked') {
								evening = 'L';
							} else {
								evening = 'B';
							}
						}
					})
				}
			}
			return [morning, evening];
		}

		get bgColor() {
			try {
				let cssVariable = getComputedStyle(document.documentElement).getPropertyValue('--multiday-calendar-bg-color--booked');
				return cssVariable ? cssVariable.trim().substr(0,7) : '#FDCC92';
			} catch (e) {
				return '#FDCC92';
			}
		}

		get bgColorLocked() {
			try {
				let cssVariable = getComputedStyle(document.documentElement).getPropertyValue('--multiday-calendar-bg-color--locked');
				return cssVariable ? cssVariable.trim().substr(0,7) : '#FDCC92';
			} catch (e) {
				return '#FDCC92';
			}
		}

		get bgColorNew() {
			try {
				let cssVariable = getComputedStyle(document.documentElement).getPropertyValue('--multiday-calendar-bg-color--new');
				return cssVariable ? cssVariable.trim().substr(0,7) : '#ffdab8';
			} catch (e) {
				return '#ffdab8';
			}
		}

		get bgColorSelected() {
			try {
				let cssVariable = getComputedStyle(document.documentElement).getPropertyValue('--multiday-calendar-bg-color--selected');
				return cssVariable ? cssVariable.trim().substr(0,7) : '#007bff';
			} catch (e) {
				return '#007bff';
			}
		}

		get bgColorFree() {
			try {
				let cssVariable = getComputedStyle(document.documentElement).getPropertyValue('--multiday-calendar-bg-color--free');
				return cssVariable ? cssVariable.trim().substr(0,7) : '#ffffff';
			} catch (e) {
				return '#ffffff';
			}
		}

		get dayStyle(): Record<string, string> | undefined {
			if (this.day && this.day.isCurrentMonth && !this.day.isPast) {
				let state = this.reservationState;
				// only use svg for half days
				if ((state[0] !== 'F' || state[1] !== 'F') && state[0] !== state[1]) {
					let colors: any = {
						B: this.bgColor,
						N: this.bgColorNew,
						L: this.bgColorLocked,
						F: this.bgColorFree,
						S: this.bgColorSelected
					}
					let image = `<svg xmlns='http://www.w3.org/2000/svg' width="200px" height="200px" viewBox="0 0 200 200">
									<polygon fill="${colors[state[0]]}" points="200 0 0 0 0 200"></polygon>
									<polygon fill="${colors[state[1]]}" points="200 0 200 200 0 200"></polygon>
								</svg>`;
					return {
						'background': this.bgColor + ' url("data:image/svg+xml;utf8,' + encodeURIComponent(image) + '") 50% 50%'
					};
				}
			}
			return undefined;
		}

		get hasReservationsPopover(): boolean {
			if (!this.day || !this.resource.reservation_show_details) {
				return false;
			}
			return this.availability && this.availability[this.day.dateKey] && this.availability[this.day.dateKey].length > 0;
		}

		public get reservationsPopoverAvailability(): IAvailabilityValue[] {
			if (this.availability && this.day && this.availability[this.day.dateKey]) {
				return this.availability[this.day.dateKey];
			}
			return []
		}

		public formatTime(minutes: number): string {
			let mins: number|string = minutes % 60;
			if (mins < 10) {
				mins = "0" + mins;
			}
			let hours: number|string = Math.floor(minutes / 60);
			if (hours < 10) {
				hours = "0" + hours;
			}
			return hours + ':' + mins;
		}
	}
